import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Cookie Consent Banner Component
 * Implements Google Consent Mode v2 for GDPR compliance
 * Manages user consent for Analytics and AdSense
 */
const CookieConsentBanner: React.FC = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem('cookie-consent');
    if (consent) {
      const consentData = JSON.parse(consent);
      setConsentGiven(true);
      setShowBanner(false);
      // Only load if ads/analytics are enabled
      if (consentData.ads || consentData.analytics) {
        loadAnalyticsAndAds();
      }
    } else {
      setShowBanner(true);
      // Initialize Google Consent Mode v2 with defaults (deny)
      initializeConsentMode(false);
    }
  }, []);

  /**
   * Initialize Google Consent Mode v2
   */
  const initializeConsentMode = (userConsent: boolean) => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'default', {
        'ad_user_data': userConsent ? 'granted' : 'denied',
        'ad_personalization': userConsent ? 'granted' : 'denied',
        'ad_storage': userConsent ? 'granted' : 'denied',
        'analytics_storage': userConsent ? 'granted' : 'denied',
      });
    }
  };

  /**
   * Load Analytics and AdSense scripts
   */
  const loadAnalyticsAndAds = () => {
    // Google Analytics (gtag.js)
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-Y94S65YBYY';
    gtagScript.onload = () => {
      if (typeof window !== 'undefined') {
        (window as any).dataLayer = (window as any).dataLayer || [];
        const gtag = (...args: any[]) => {
          (window as any).dataLayer.push(args);
        };
        (window as any).gtag = gtag;
        gtag('js', new Date());
        gtag('config', 'G-Y94S65YBYY', {
          'anonymize_ip': true,
          'cookie_flags': 'SameSite=None;Secure'
        });
      }
    };
    document.head.appendChild(gtagScript);

    // Google AdSense (async)
    // Check if script already exists to prevent duplicate loading
    const existingAdsenseScript = document.querySelector('script[src*="adsbygoogle"]');
    if (!existingAdsenseScript) {
      const adsenseScript = document.createElement('script');
      adsenseScript.async = true;
      adsenseScript.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-9221349490619432';
      adsenseScript.setAttribute('crossorigin', 'anonymous');
      adsenseScript.onload = () => {
        if (typeof window !== 'undefined' && (window as any).adsbygoogle) {
          try {
            ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
          } catch (e) {
            console.warn('AdSense already initialized or no ads on page');
          }
        }
      };
      document.head.appendChild(adsenseScript);
    }
  };

  /**
   * Handle accept all consent
   */
  const handleAcceptAll = () => {
    localStorage.setItem('cookie-consent', JSON.stringify({
      timestamp: new Date().toISOString(),
      analytics: true,
      ads: true,
      version: '1.0',
    }));
    initializeConsentMode(true);
    loadAnalyticsAndAds();
    setShowBanner(false);
    setConsentGiven(true);
  };

  /**
   * Handle reject all consent
   */
  const handleRejectAll = () => {
    localStorage.setItem('cookie-consent', JSON.stringify({
      timestamp: new Date().toISOString(),
      analytics: false,
      ads: false,
      version: '1.0',
    }));
    initializeConsentMode(false);
    setShowBanner(false);
    setConsentGiven(true);
  };

  if (!showBanner || consentGiven) {
    return null;
  }

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900 border-t border-slate-700 p-4 sm:p-6"
      role="region"
      aria-label={t('common.cookieConsent') || 'Cookie Consent'}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-white font-semibold mb-2">
              {t('cookieConsent.title') || 'Cookie Consent'}
            </h3>
            <p className="text-sm text-slate-300 mb-3 sm:mb-0">
              {t('cookieConsent.description') ||
                'We use cookies and similar technologies to improve your experience, analyze traffic, and show relevant ads. By accepting, you consent to our use of these technologies.'}
            </p>
            <a
              href="/privacy"
              className="text-blue-400 hover:text-blue-300 text-sm underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('footer.privacy') || 'Privacy Policy'}
            </a>
          </div>

          <div className="flex gap-3 sm:flex-shrink-0">
            <button
              onClick={handleRejectAll}
              className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded transition-colors"
              aria-label={t('cookieConsent.rejectAll') || 'Reject All'}
            >
              {t('cookieConsent.rejectAll') || 'Reject'}
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
              aria-label={t('cookieConsent.acceptAll') || 'Accept All'}
            >
              {t('cookieConsent.acceptAll') || 'Accept All'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
